/**
 * The analytics event catalog: one entry per GA4 event that any platform
 * sends, and the single source the web taxonomy, the Pixel mapping and the
 * cross-platform guards read.
 *
 * - `name` — the GA4 event name. Product-flavoured; it MUST NOT reveal the
 *   AI/STT tech stack (no provider or model names).
 * - `platforms` — who sends it: `web` (gtag in the browser), `ios` / `android`
 *   (Firebase SDK in the apps), `server` (GA4 Measurement Protocol from
 *   `apps/api` to the web stream) and `desktop` (the desktop app has no GA4
 *   SDK: `apps/api` forwards its events to the GA4 desktop stream, see
 *   `apps/api/app/services/analytics/desktop_ga4.ts`). An entry may also name
 *   a platform that is decided but not shipped yet;
 *   `scripts/ci/analytics-platform-coverage.known-gaps.json` lists those, and
 *   `scripts/ci/analytics-platform-coverage.test.mjs` checks both directions
 *   against each platform's code.
 * - `params` — the params the event may carry, each in `params.ts`. The web
 *   dispatch also stamps `anon_id` on every GA payload.
 * - `meta` — the Meta event per sender: `web` (browser Pixel), `app`
 *   (Facebook App Events from iOS/Android), `server` (Conversions API, also
 *   on behalf of the desktop app). A
 *   missing sender sends nothing to Meta. `standard: true` is a Meta standard
 *   event Meta may optimise ad delivery toward; everything else is custom.
 * - `legacy` — shipped before the naming rules; renaming it would cut its GA4
 *   history, so the naming rules skip it. New events must not copy it.
 * - `key` — present on the web semantic taxonomy fired through `trackEvent`
 *   (`ANALYTICS_EVENTS` in apps/web); those must carry `meta.web`.
 *
 * Naming rules and the reasons behind them: `.claude/skills/event-conventions`.
 * The native contract tests read this array from source
 * (`scripts/lib/analytics-catalog-source.mjs`), so it stays a plain literal:
 * no identifiers, calls or spreads inside it.
 */
import type { AnalyticsCatalogParamName } from './params.js'

export const ANALYTICS_PLATFORMS = ['web', 'ios', 'android', 'desktop', 'server'] as const

export type AnalyticsPlatform = (typeof ANALYTICS_PLATFORMS)[number]

/** A Meta event: `fbq('track' | 'trackCustom', name)`, App Events or CAPI. */
export interface AnalyticsMetaEvent {
  readonly name: string
  readonly standard: boolean
}

export interface AnalyticsEventMeta {
  readonly web?: AnalyticsMetaEvent
  readonly app?: AnalyticsMetaEvent
  readonly server?: AnalyticsMetaEvent
}

interface AnalyticsEventBase {
  readonly name: string
  readonly platforms: readonly AnalyticsPlatform[]
  readonly params: readonly AnalyticsCatalogParamName[]
  readonly legacy?: boolean
}

interface WebTrackedEventDefinition extends AnalyticsEventBase {
  readonly key: string
  readonly meta: AnalyticsEventMeta & { readonly web: AnalyticsMetaEvent }
}

interface UntrackedEventDefinition extends AnalyticsEventBase {
  readonly key?: undefined
  readonly meta?: AnalyticsEventMeta
}

export type AnalyticsEventDefinition = WebTrackedEventDefinition | UntrackedEventDefinition

export const ANALYTICS_EVENT_CATALOG = [
  // ── Web semantic taxonomy (`trackEvent`, GA4 + Pixel) ──────────────────────
  {
    /** Hero "download" CTA clicked (smooth-scrolls to the #download section). Carries `location` (`hero`). */
    name: 'landing_download_cta_click',
    key: 'landingDownloadCtaClick',
    platforms: ['web'],
    params: ['location'],
    meta: { web: { name: 'LandingDownloadCtaClick', standard: false } },
    legacy: true,
  },
  {
    /** Real app download link clicked. Carries `platform` (macos/windows/android/ios) + `location` (`download`/`footer`). */
    name: 'download_app_click',
    key: 'downloadAppClick',
    platforms: ['web'],
    params: ['platform', 'location'],
    meta: { web: { name: 'DownloadApp', standard: false } },
    legacy: true,
  },
  {
    /** Visitor took over the hero live demo (clicked the mic to actually try it) — the strongest "interested" signal on the landing page. */
    name: 'hero_demo_interact',
    key: 'heroDemoInteract',
    platforms: ['web'],
    params: [],
    meta: { web: { name: 'HeroDemoInteract', standard: false } },
    legacy: true,
  },
  {
    /** The hero live demo produced a real transcript for the visitor (the "aha" moment). */
    name: 'hero_demo_result_shown',
    key: 'heroDemoResultShown',
    platforms: ['web'],
    params: [],
    meta: { web: { name: 'HeroDemoResultShown', standard: false } },
    legacy: true,
  },
  {
    /** Hero "try the demo" CTA clicked — the assist that routes attention to the live demo. Carries `location` (`hero`). */
    name: 'landing_try_demo_click',
    key: 'landingTryDemoClick',
    platforms: ['web'],
    params: ['location'],
    meta: { web: { name: 'LandingTryDemoClick', standard: false } },
    legacy: true,
  },
  {
    /** Pricing plan card body clicked (highlights/considers a plan — softer than the CTA). Carries `plan` + `cycle`. */
    name: 'pricing_plan_card_click',
    key: 'pricingPlanCardClick',
    platforms: ['web'],
    params: ['plan', 'cycle'],
    meta: { web: { name: 'PricingPlanCardClick', standard: false } },
    legacy: true,
  },
  {
    /** Pricing billing-cycle toggle flipped. Carries `cycle`. */
    name: 'pricing_cycle_toggle',
    key: 'pricingCycleToggle',
    platforms: ['web'],
    params: ['cycle'],
    meta: { web: { name: 'PricingCycleToggle', standard: false } },
    legacy: true,
  },
  {
    /** Pricing free-plan CTA clicked (routes to the free trial). */
    name: 'pricing_free_cta_click',
    key: 'pricingFreeCtaClick',
    platforms: ['web'],
    params: [],
    meta: { web: { name: 'PricingFreeCtaClick', standard: false } },
    legacy: true,
  },
  {
    /** Pricing credit-pack CTA clicked. */
    name: 'pricing_credit_pack_cta_click',
    key: 'pricingCreditPackCtaClick',
    platforms: ['web'],
    params: [],
    meta: { web: { name: 'PricingCreditPackCtaClick', standard: false } },
    legacy: true,
  },
  {
    /** Pricing team CTA clicked. */
    name: 'pricing_team_cta_click',
    key: 'pricingTeamCtaClick',
    platforms: ['web'],
    params: ['cycle'],
    meta: { web: { name: 'PricingTeamCtaClick', standard: false } },
    legacy: true,
  },
  {
    /**
     * Guest opened the OS file picker. Carries `source` (button/dropzone).
     *
     * `trial_upload_started` only fires once a file has passed format + duration
     * validation, so on its own it cannot tell "never opened the picker" (no
     * file to hand) from "opened it and picked nothing" from "picked something
     * that got rejected". This event and the two below split those apart, which
     * is what locates the drop-off before anyone redesigns the page.
     */
    name: 'trial_file_picker_opened',
    key: 'trialFilePickerOpened',
    platforms: ['web'],
    params: ['source'],
    // Diagnostic funnel events — deliberately custom: they measure friction, not
    // conversion, so Meta must not optimise delivery toward them.
    meta: { web: { name: 'TrialFilePickerOpened', standard: false } },
  },
  {
    /** Guest actually chose a file — fired before any validation runs. */
    name: 'trial_file_selected',
    key: 'trialFileSelected',
    platforms: ['web'],
    params: [],
    meta: { web: { name: 'TrialFileSelected', standard: false } },
  },
  {
    /**
     * The chosen file was rejected — by the browser's own checks or by the
     * server's authoritative ones. Carries `reason` (unsupported_format /
     * duration_unreadable / file_too_large).
     *
     * A rejection is the visitor being told no about the FILE, which is a
     * different problem from the upload breaking: it is answered by changing what
     * we accept or how we say so, never by fixing an outage. `trial_upload_failed`
     * is the other half, and no attempt reports both.
     */
    name: 'trial_file_rejected',
    key: 'trialFileRejected',
    platforms: ['web'],
    params: ['reason'],
    meta: { web: { name: 'TrialFileRejected', standard: false } },
  },
  {
    /** Guest started a free trial upload (valid file, within the trial cap). */
    name: 'trial_upload_started',
    key: 'trialUploadStarted',
    platforms: ['web'],
    params: [],
    meta: { web: { name: 'TrialUploadStarted', standard: false } },
  },
  {
    /**
     * An accepted file's upload never reached the result page — the missing half
     * of `trial_upload_started`, which until now was followed by silence whenever
     * anything went wrong below the two rejection branches.
     *
     * It answers "of the people who committed to a real upload, how many got
     * nothing, and was it us or the road" — one row per failed ATTEMPT, so a
     * visitor who retries three times is three rows and the retry count is itself
     * the signal. A cancel is not a failure and reports nothing.
     *
     * `reason` is the whole set: `rate_limited`, `trial_disabled`,
     * `upload_stalled`, `upload_not_found`, `network`, `server_error`,
     * `unexpected_response`, `other` — classified once in
     * `features/anonymous-trial/upload-failure-reason.ts` and shared with the
     * Sentry report fired beside it. Also carries `status` (when there was one),
     * `code` and `phase`. Never a filename.
     */
    name: 'trial_upload_failed',
    key: 'trialUploadFailed',
    platforms: ['web'],
    params: ['reason', 'status', 'code', 'phase'],
    // Custom, not standard: a failed upload is a breakage. Feeding it to Meta as
    // a conversion would tell delivery to go find more people who will fail.
    meta: { web: { name: 'TrialUploadFailed', standard: false } },
  },
  {
    /**
     * The guest's upload finished transcribing while they were still on the page
     * that started it — the third state beside `trial_upload_started` and
     * `trial_upload_failed`, so the success rate no longer depends on whether the
     * visitor also clicked through to the result page (`trial_result_viewed`).
     *
     * Mirrored to `anonymous_events`: `anonymous_trials` has no `kv_did` to join
     * back to the browser and its rows are purged 24 hours after creation, so this
     * row is the only completion record that outlives the day. No params.
     */
    name: 'trial_upload_completed',
    key: 'trialUploadCompleted',
    platforms: ['web'],
    params: [],
    meta: { web: { name: 'TrialUploadCompleted', standard: false } },
  },
  {
    /** Guest's trial transcript finished and is shown — top of the conversion funnel. Carries `short_id`. */
    name: 'trial_result_viewed',
    key: 'trialResultViewed',
    platforms: ['web'],
    params: ['short_id'],
    meta: { web: { name: 'ViewContent', standard: true } },
  },
  {
    /** Trial transcript downloaded (vtt/srt) — requires a session (post-signup). */
    name: 'trial_transcript_download',
    key: 'trialTranscriptDownload',
    platforms: ['web'],
    params: ['format'],
    meta: { web: { name: 'TrialTranscriptDownload', standard: false } },
    legacy: true,
  },
  {
    /** Trial transcript copied to clipboard — requires a session (post-signup). */
    name: 'trial_transcript_copy',
    key: 'trialTranscriptCopy',
    platforms: ['web'],
    params: [],
    meta: { web: { name: 'TrialTranscriptCopy', standard: false } },
    legacy: true,
  },
  {
    /**
     * A /pricing link on the guest trial result page was clicked. Carries two
     * params, and BOTH are needed to read the numbers.
     *
     * `location` — which of the four entries on that ONE page was clicked. It is
     * the SAME param the download/demo CTAs use for this question (see
     * `landing_download_cta_click`, `download_app_click`, `landing_try_demo_click`
     * above), so cross-entry comparisons read one dimension, not one per event.
     * Values: `trim_notice` (beside the "only the first N minutes" line at the
     * top), `trim_end` (beside the end-of-transcript marker), `perk_strip` (the
     * page footer) and `signup_done` (the panel shown after in-page signup).
     *
     * `status` — the trial's status at click time. The four entries are NOT
     * equally exposed: `trim_notice` and `perk_strip` are already on screen while
     * the visitor is watching the poller (the trim length is stamped at submit
     * time, so the notice does not wait for the transcript), while `trim_end`
     * exists only once it completed and `signup_done` only after signup. That
     * asymmetry is a deliberate product choice — reading "only the first 5
     * minutes" while waiting is exactly when someone weighs a plan, so the entry
     * stays and the measurement adapts instead. Comparing raw `location` totals
     * therefore overstates the two always-on entries; hold `status` fixed
     * (`status=completed` is the fair field, where all four can appear) and
     * compare `location` within it.
     *
     * Why the event exists at all: /pricing's own `view_item_list` already counts
     * arrivals and GA's `page_referrer` narrows them to this page, but all four
     * entries share that one referrer, so neither can say WHICH entry sent them
     * — the whole question when deciding where a pricing entry belongs.
     */
    name: 'trial_result_pricing_click',
    key: 'trialResultPricingClick',
    platforms: ['web'],
    params: ['location', 'status'],
    // Custom, not standard: looking at the price list is navigation, not a
    // purchase. Handing Meta a conversion here would optimise delivery toward
    // people who read prices and leave — the cheapest half of the funnel.
    meta: { web: { name: 'TrialResultPricingClick', standard: false } },
    legacy: true,
  },
  {
    /**
     * The magic link in that email was used: the address is now verified, the
     * account is provisioned, and the transcript is attached. This is the event
     * that separates a real lead from an address someone typed.
     */
    name: 'trial_transcript_email_claimed',
    key: 'trialTranscriptEmailClaimed',
    platforms: ['web'],
    params: [],
    // Verified + provisioned — the same milestone as finishing OTP signup.
    meta: { web: { name: 'CompleteRegistration', standard: true } },
  },
  {
    /**
     * The link signed them in but attaching the transcript failed (expired trial,
     * already claimed, API down). Without its own event the claim funnel only
     * ever reports successes, so a systematic breakage would look like nobody
     * clicking rather than everybody failing.
     */
    name: 'trial_transcript_email_claim_failed',
    key: 'trialTranscriptEmailClaimFailed',
    platforms: ['web'],
    params: [],
    // Custom, not standard: this measures a breakage, and feeding it to Meta as a
    // conversion would optimise delivery toward people whose claims fail.
    meta: { web: { name: 'TrialTranscriptEmailClaimFailed', standard: false } },
  },
  {
    /**
     * A campaign panel came into view. The DENOMINATOR of the campaign funnel:
     * without it, a campaign with few claims is indistinguishable from one nobody
     * scrolled far enough to see. Carries `slug`, `surface` and `state` — the
     * same panel converts very differently when it reads 額滿 rather than 開放中.
     */
    name: 'campaign_viewed',
    key: 'campaignViewed',
    platforms: ['web'],
    params: ['slug', 'surface', 'state'],
    // Custom: an impression is measurement, not something Meta should optimise
    // delivery toward.
    meta: { web: { name: 'CampaignViewed', standard: false } },
  },
  {
    /** Visitor's address passed format validation and the OTP modal opened. Carries `slug`, `surface`. */
    name: 'campaign_email_submitted',
    key: 'campaignEmailSubmitted',
    platforms: ['web'],
    params: ['slug', 'surface'],
    // A campaign address is a captured lead exactly like the other two paths, so
    // it feeds the same standard event and campaigns optimise across all of them.
    meta: { web: { name: 'Lead', standard: true } },
  },
  {
    /**
     * The grant landed. Carries `slug`, `surface`, `path` (otp/signed_in) and
     * `account_created`; the split is what tells a campaign that ACQUIRES from
     * one that only re-activates the people already on the list.
     */
    name: 'campaign_claimed',
    key: 'campaignClaimed',
    platforms: ['web'],
    params: ['slug', 'surface', 'path', 'account_created'],
    // StartTrial rather than CompleteRegistration: the account is only the
    // vehicle — what the visitor actually got is a free run of a paid plan, and
    // that is the audience worth optimising toward.
    meta: { web: { name: 'StartTrial', standard: true } },
  },
  {
    /**
     * The claim call failed. Carries `reason` (the API's error code). A campaign
     * that fills up, or one breaking for everyone, looks identical from the
     * success events alone — both simply stop producing claims.
     */
    name: 'campaign_claim_failed',
    key: 'campaignClaimFailed',
    platforms: ['web'],
    params: ['slug', 'surface', 'reason'],
    // Custom, not standard: this measures a refusal, and handing it to Meta as a
    // conversion would optimise delivery toward people whose claims fail.
    meta: { web: { name: 'CampaignClaimFailed', standard: false } },
  },
  {
    /** Waitlist CTA on a full/capped campaign clicked. Carries `slug`, `surface`. */
    name: 'campaign_waitlist_click',
    key: 'campaignWaitlistClick',
    platforms: ['web'],
    params: ['slug', 'surface'],
    meta: { web: { name: 'CampaignWaitlistClick', standard: false } },
    legacy: true,
  },
  {
    /** Guest hit a gated action and got nudged toward signup. */
    name: 'signup_nudge_shown',
    key: 'signupNudgeShown',
    platforms: ['web'],
    params: [],
    meta: { web: { name: 'SignupNudgeShown', standard: false } },
    legacy: true,
  },
  {
    /** Guest submitted their email in the in-page signup (OTP requested). */
    name: 'signup_email_submitted',
    key: 'signupEmailSubmitted',
    platforms: ['web'],
    params: ['source'],
    // Lead = email captured; CompleteRegistration = account created. Both are
    // standard Pixel events campaigns are typically optimised toward.
    meta: { web: { name: 'Lead', standard: true } },
  },
  {
    /**
     * Guest completed signup (OTP verified and `accountWasCreated`) — the key
     * conversion event. The apps send it too, with `method` and `source`; the
     * shipped name is kept rather than GA4's `sign_up`.
     */
    name: 'signup_completed',
    key: 'signupCompleted',
    platforms: ['web', 'ios', 'android'],
    params: ['source', 'method'],
    meta: {
      web: { name: 'CompleteRegistration', standard: true },
      app: { name: 'CompleteRegistration', standard: true },
    },
  },
  {
    /**
     * A link or CTA on the marketing site was clicked. One event for every
     * in-site navigation so "which entry sends people where" is a single query:
     * `location` is where on the page it sat (`header`, `header_products`,
     * `mobile_menu`, `footer`, `hero`, `end_cta`, `faq`, or a section's
     * `trackingKey` such as `services`), `target` is where it goes (`/transcription`,
     * `/transcription#try`, `#try`). The page is GA4's own `page_location`.
     */
    name: 'site_link_clicked',
    key: 'siteLinkClicked',
    platforms: ['web'],
    params: ['location', 'target'],
    // Navigation and reading are measurement, not conversions Meta should
    // optimise delivery toward.
    meta: { web: { name: 'SiteLinkClicked', standard: false } },
  },
  {
    /**
     * A marketing section was seen — at least half of it on screen, or it filled
     * half the screen (a section taller than two screens can never be half
     * visible), continuously for one second so a section a smooth scroll carries
     * past does not count — once per section per page view. Carries `location` (the section's `trackingKey`), the same value the
     * clicks inside that section report, so "saw it" and "clicked in it" join on
     * one field. It is the denominator of every entry's click-through rate.
     */
    name: 'site_section_viewed',
    key: 'siteSectionViewed',
    platforms: ['web'],
    params: ['location'],
    meta: { web: { name: 'SiteSectionViewed', standard: false } },
  },
  {
    /**
     * The visitor first operated a product demo on a marketing page — once per
     * demo per page view. Carries `demo` (`subtitle`, `meeting_editor`,
     * `meeting_outputs`, `notes_templates`). The dictation demo keeps its older
     * `hero_demo_interact`.
     */
    name: 'product_demo_started',
    key: 'productDemoStarted',
    platforms: ['web'],
    params: ['demo'],
    meta: { web: { name: 'ProductDemoStarted', standard: false } },
  },
  {
    /**
     * A FAQ entry was expanded (not collapsed). Carries `question`, the entry's
     * fixed key — never its text — so a copy edit does not split the series.
     */
    name: 'faq_opened',
    key: 'faqOpened',
    platforms: ['web'],
    params: ['question'],
    meta: { web: { name: 'FaqOpened', standard: false } },
  },
  {
    /**
     * A/B test exposure — the visitor was actually assigned a variant. Fired by
     * GrowthBook's trackingCallback and carries `experiment_id` + `variation_id`;
     * those exact field names are what GrowthBook's GA4 data source reads, so
     * don't rename them. This is the DENOMINATOR of every experiment result.
     */
    name: 'experiment_viewed',
    key: 'experimentViewed',
    platforms: ['web'],
    params: ['experiment_id', 'variation_id'],
    // Custom: an exposure is measurement, not a conversion Meta should optimise
    // ad delivery toward.
    meta: { web: { name: 'ExperimentViewed', standard: false } },
  },
  {
    /**
     * A new member answered the「你最想先用 KIAO Voice 做什麼？」question on the
     * empty transcripts list. Carries `use_case` (`meeting`, `lecture`, `media`,
     * `dictation`, `other`). The server stores the same answer on the account;
     * this event is what lets GA4 put the answer next to the upload and signup
     * events that follow it.
     */
    name: 'use_case_selected',
    key: 'useCaseSelected',
    platforms: ['web'],
    params: ['use_case'],
    // Custom: a self-reported intent from someone already signed up is a
    // segmentation fact, not a conversion Meta should optimise ad delivery toward.
    meta: { web: { name: 'UseCaseSelected', standard: false } },
  },
  {
    /** The member skipped that question instead of answering it. No params. */
    name: 'use_case_question_dismissed',
    key: 'useCaseQuestionDismissed',
    platforms: ['web'],
    params: [],
    meta: { web: { name: 'UseCaseQuestionDismissed', standard: false } },
  },

  // ── GA4 ecommerce (web `commerce.ts`, the apps' paywall, the server) ───────
  {
    /**
     * Plans list shown: the web pricing page or plans modal, and the apps'
     * paywall once its plans load (`source` is the paywall trigger).
     */
    name: 'view_item_list',
    platforms: ['web', 'ios', 'android'],
    params: ['item_list_name', 'items', 'source'],
    meta: { web: { name: 'ViewContent', standard: true } },
  },
  {
    /** A specific plan picked on the web; sent together with `add_to_cart`. */
    name: 'select_item',
    platforms: ['web'],
    params: ['item_list_name', 'items'],
  },
  {
    /** A specific plan picked on the web (the cart step of the checkout funnel). */
    name: 'add_to_cart',
    platforms: ['web'],
    params: ['currency', 'value', 'items'],
    meta: { web: { name: 'AddToCart', standard: true } },
  },
  {
    /** Checkout opened: the web payment form, or the app's subscribe button. */
    name: 'begin_checkout',
    platforms: ['web', 'ios', 'android'],
    params: ['currency', 'value', 'items'],
    meta: { web: { name: 'InitiateCheckout', standard: true } },
  },
  {
    /** Card details submitted on the web — payment info added, about to charge. */
    name: 'add_payment_info',
    platforms: ['web'],
    params: ['currency', 'value', 'items'],
    meta: { web: { name: 'AddPaymentInfo', standard: true } },
  },
  {
    /**
     * Payment succeeded. Web fires it only on the non-3DS success path (the
     * Pixel `Purchase` carries the CAPI dedup id); the server sends it for TapPay
     * charges with `purchase_type` (`initial`/`renewal`), plus a CAPI `Subscribe`
     * for subscriptions; iOS sends it for an Apple first purchase delivered on
     * that phone and sends nothing to Meta.
     */
    name: 'purchase',
    platforms: ['web', 'ios', 'server'],
    params: ['transaction_id', 'currency', 'value', 'items', 'purchase_type'],
    meta: {
      web: { name: 'Purchase', standard: true },
      server: { name: 'Purchase', standard: true },
    },
  },
  {
    /**
     * Web payment failed or was declined — checkout drop-off analysis. Borrows
     * GA4's ecommerce params (`currency`, `value`, top-level `item_id`), which a
     * custom event must not do; new failure events use `reason` and `_twd`.
     */
    name: 'purchase_failed',
    platforms: ['web'],
    params: ['currency', 'value', 'item_id', 'reason'],
    meta: { web: { name: 'PurchaseFailed', standard: false } },
  },

  // ── Respondent survey (web `features/survey-respond`, GA only) ─────────────
  {
    /** The intro rendered for a survey that exists: the denominator of the funnel. */
    name: 'survey_viewed',
    platforms: ['web'],
    params: ['survey_slug', 'revision'],
  },
  {
    /** This respondent's response was created now (the API answered 201, not a resume). */
    name: 'survey_started',
    platforms: ['web'],
    params: ['survey_slug', 'revision'],
  },
  {
    /** A question came into view; fires again when the respondent returns to it. */
    name: 'survey_question_viewed',
    platforms: ['web'],
    params: ['survey_slug', 'revision', 'question_id', 'position'],
  },
  {
    /** The respondent left a question answered or skipped for the first time. */
    name: 'survey_question_completed',
    platforms: ['web'],
    params: ['survey_slug', 'revision', 'question_id', 'position', 'outcome'],
  },
  {
    /** The server accepted the submission. */
    name: 'survey_submitted',
    platforms: ['web'],
    params: ['survey_slug', 'revision'],
  },

  // ── App events (iOS and Android through the Firebase SDK) ──────────────────
  // When each one fires is documented on its case in
  // apps/ios/KiaoVoice/Analytics/analytics-events.swift.
  {
    /**
     * Onboarding's first screen appeared, replays included. The desktop reports
     * it as the `user_events` row `tutorial_begin` once the person signs in.
     */
    name: 'tutorial_begin',
    platforms: ['ios', 'android', 'desktop'],
    params: ['is_replay', 'app_version'],
  },
  {
    /** The AI consent screen was answered (iOS only so far). */
    name: 'ai_consent_selected',
    platforms: ['ios'],
    params: ['state', 'is_replay'],
  },
  {
    /** The system microphone prompt was answered. */
    name: 'microphone_permission_selected',
    platforms: ['ios', 'android'],
    params: ['state', 'source', 'is_replay'],
  },
  {
    /** The App Tracking Transparency prompt was answered (iOS only: no ATT on Android). */
    name: 'tracking_permission_selected',
    platforms: ['ios'],
    params: ['state', 'is_replay'],
  },
  {
    /** An onboarding step appeared. */
    name: 'onboarding_step_viewed',
    platforms: ['ios', 'android'],
    params: ['step', 'is_replay'],
  },
  {
    /** A practice recording started on the onboarding try step. */
    name: 'onboarding_try_started',
    platforms: ['ios', 'android'],
    params: ['attempt_count', 'is_replay'],
  },
  {
    /** A practice recording produced a result. */
    name: 'onboarding_try_completed',
    platforms: ['ios', 'android'],
    params: ['is_suggested_sentence', 'attempt_count', 'is_replay'],
  },
  {
    /** A practice recording failed. */
    name: 'onboarding_try_failed',
    platforms: ['ios', 'android'],
    params: ['reason', 'is_replay'],
  },
  {
    /** The user moved forward off an onboarding step. */
    name: 'onboarding_step_completed',
    platforms: ['ios', 'android'],
    params: ['step', 'method', 'is_replay', 'choice', 'state'],
  },
  {
    /** "Open Settings" for the keyboard was tapped. */
    name: 'keyboard_settings_clicked',
    platforms: ['ios', 'android'],
    params: ['source', 'is_replay'],
  },
  {
    /** Onboarding was skipped. */
    name: 'onboarding_dismissed',
    platforms: ['ios', 'android'],
    params: ['step', 'is_replay'],
  },
  {
    /**
     * Onboarding was finished (skipping does not count). Replays send nothing to
     * Meta. The desktop's comes from the `user_events` row
     * `onboarding_completed`, with its `method`.
     */
    name: 'tutorial_complete',
    platforms: ['ios', 'android', 'desktop'],
    params: ['choice', 'is_replay', 'method', 'app_version'],
    meta: { app: { name: 'CompleteTutorial', standard: true } },
  },
  {
    /** The keyboard handshake succeeded for the first time on this phone. Replays send nothing to Meta. */
    name: 'keyboard_setup_completed',
    platforms: ['ios', 'android'],
    params: ['source', 'is_replay'],
    meta: { app: { name: 'KeyboardSetupCompleted', standard: false } },
  },
  {
    /** The "Live Activities are off" notice appeared (iOS only). */
    name: 'live_activity_notice_viewed',
    platforms: ['ios'],
    params: ['source'],
  },
  {
    /** "Open Settings" on that notice was tapped (iOS only). */
    name: 'live_activity_settings_clicked',
    platforms: ['ios'],
    params: ['source'],
  },
  {
    /** That notice was closed (iOS only). */
    name: 'live_activity_notice_dismissed',
    platforms: ['ios'],
    params: ['source'],
  },
  {
    /** Live Activities were found on while the notice was up (iOS only). */
    name: 'live_activity_setup_completed',
    platforms: ['ios'],
    params: ['source'],
  },
  {
    /**
     * Voice input started capturing audio, every time. Not the desktop's
     * `user_events` row of the same name, which is the first hotkey press per
     * person; the desktop reports each capture on `POST /api/v1/ga4-events`.
     */
    name: 'voice_input_started',
    platforms: ['ios', 'android', 'desktop'],
    params: ['is_signed_in', 'app_version'],
  },
  {
    /** Voice input produced a non-empty transcript for insertion. */
    name: 'voice_input_completed',
    platforms: ['ios', 'android'],
    params: ['is_signed_in', 'is_first_voice_input'],
  },
  {
    /** Voice input was refused by a rule (quota, consent, permission, workspace). */
    name: 'voice_input_rejected',
    platforms: ['ios', 'android'],
    params: ['reason', 'is_signed_in'],
  },
  {
    /** Voice input did not finish because of the network or the service. */
    name: 'voice_input_failed',
    platforms: ['ios', 'android'],
    params: ['reason', 'is_signed_in'],
  },
  {
    /** The keyboard inserted voice transcripts (reported in batches when the app returns to the foreground). */
    name: 'voice_insertion_completed',
    platforms: ['ios', 'android'],
    params: ['insertion_count', 'new_insertion_count'],
  },
  {
    /**
     * After the keyboard opened the app and the voice keyboard was ready, the user was sent back
     * to the app they were typing in (iOS only). `method` is how (`bundle_id`, `url`), `state` is
     * the bundle-ID step's outcome, `elapsed_ms` runs from the key press, `host_bundle_id` is that
     * app's bundle ID. GA4 only: a diagnostic, not a conversion.
     */
    name: 'keyboard_return_completed',
    platforms: ['ios'],
    params: ['method', 'state', 'elapsed_ms', 'host_bundle_id'],
  },
  {
    /**
     * After the keyboard opened the app, the user was not sent back (iOS only). `reason` and
     * `phase` say why and where; `code` is the session start branch when `reason` is
     * `capture_failed`. Value lists are in `apps/ios/KiaoVoice/Analytics/analytics-events.swift`.
     * GA4 only.
     */
    name: 'keyboard_return_failed',
    platforms: ['ios'],
    params: ['reason', 'phase', 'code', 'state', 'elapsed_ms', 'host_bundle_id'],
  },
  // Learning dictionary terms from the user's edits to inserted voice text. The keyboard records
  // its own events without any text and the app reports them; GA4 only (diagnostics, not conversions).
  {
    /** After a voice insertion the keyboard started tracking the user's edits. */
    name: 'dictionary_learning_started',
    platforms: ['ios', 'android'],
    params: [],
  },
  {
    /**
     * Tracking ended without sending an edit. `reason` is the tracker's drop reason
     * (`contracts/dictionary-learning/edit-tracker.json`): `context_changed` (the keyboard lost
     * track of the text: what it read around the cursor no longer matched its copy, or was cleared,
     * and no edit was left to send), `too_many_changes` (more than 3 spans or over half the
     * insertion).
     */
    name: 'dictionary_learning_rejected',
    platforms: ['ios', 'android'],
    params: ['reason'],
  },
  {
    /** The app delivered an edit to `POST /dictionary-learning/observations` and got an answer. */
    name: 'dictionary_learning_submitted',
    platforms: ['ios', 'android'],
    params: [],
  },
  {
    /** The server added a term from the edit to the auto-learned dictionary. */
    name: 'dictionary_learning_completed',
    platforms: ['ios', 'android'],
    params: [],
  },
  {
    /** The user tapped "undo" on the keyboard's "added to dictionary" notice. */
    name: 'dictionary_learning_undo_clicked',
    platforms: ['ios', 'android'],
    params: [],
  },
  {
    /** A meeting recording started. */
    name: 'meeting_recording_started',
    platforms: ['ios', 'android'],
    params: ['is_signed_in', 'source'],
  },
  {
    /** Starting a meeting recording was refused by a rule. */
    name: 'meeting_recording_rejected',
    platforms: ['ios', 'android'],
    params: ['reason', 'source'],
  },
  {
    /** Starting a meeting recording failed (audio or storage). */
    name: 'meeting_recording_failed',
    platforms: ['ios', 'android'],
    params: ['reason', 'source'],
  },
  {
    /** The recording's end sheet picked where the recording goes. */
    name: 'meeting_recording_completed',
    platforms: ['ios', 'android'],
    params: ['state', 'duration_secs', 'is_signed_in'],
  },
  {
    /** A sign-in screen appeared. */
    name: 'signin_sheet_viewed',
    platforms: ['ios', 'android'],
    params: ['source'],
  },
  {
    /** The meeting sign-in sheet was closed. */
    name: 'signin_sheet_dismissed',
    platforms: ['ios', 'android'],
    params: ['reason'],
  },
  {
    /** A well-formed email was submitted for sign-in. */
    name: 'signin_email_submitted',
    platforms: ['ios', 'android'],
    params: ['source'],
  },
  {
    /** A sign-in code was submitted. */
    name: 'signin_code_submitted',
    platforms: ['ios', 'android'],
    params: ['source'],
  },
  {
    /** A sign-in code was refused. */
    name: 'signin_code_rejected',
    platforms: ['ios', 'android'],
    params: ['reason', 'source'],
  },
  {
    /** A sign-in code could not be verified because of the network or the service. */
    name: 'signin_code_failed',
    platforms: ['ios', 'android'],
    params: ['reason', 'source'],
  },
  {
    /**
     * An existing account signed in (OTP verified). The server sends the
     * desktop's first sign-in per account to the desktop stream, with the Meta
     * name `DesktopLogin` (`shouldMirror` in `user_events_service.ts`).
     */
    name: 'login',
    // value-list: local — the platforms that send `login`; equal to CLIENT_VERSIONS by coincidence.
    platforms: ['ios', 'android', 'web', 'desktop'],
    params: ['method', 'source', 'event_id', 'app_version'],
    meta: { server: { name: 'DesktopLogin', standard: false } },
  },
  {
    /** A meeting upload job was created. */
    name: 'meeting_upload_started',
    platforms: ['ios', 'android'],
    params: ['source'],
  },
  {
    /** The server created the batch. Only the first meeting goes to Meta. */
    name: 'meeting_upload_completed',
    platforms: ['ios', 'android'],
    params: ['is_first_meeting', 'duration_secs'],
    meta: { app: { name: 'MeetingUploadCompleted', standard: false } },
  },
  {
    /** An upload stayed on the device, refused by a rule. */
    name: 'meeting_upload_rejected',
    platforms: ['ios', 'android'],
    params: ['reason'],
  },
  {
    /** An upload stayed on the device because the file is gone. */
    name: 'meeting_upload_failed',
    platforms: ['ios', 'android'],
    params: ['reason'],
  },
  {
    /** A meeting import picked its source. */
    name: 'meeting_import_started',
    platforms: ['ios', 'android'],
    params: ['method'],
  },
  {
    /** A meeting's notes tab opened with content (once per meeting). */
    name: 'meeting_notes_viewed',
    platforms: ['ios', 'android'],
    params: [],
  },
  {
    /** A main app screen appeared (automatic screen reporting is off). */
    name: 'screen_view',
    platforms: ['ios', 'android'],
    params: ['screen_name', 'screen_class'],
  },
  {
    /** The satisfaction sheet appeared (iOS only so far). */
    name: 'satisfaction_prompt_viewed',
    platforms: ['ios'],
    params: ['source'],
  },
  {
    /** The satisfaction sheet was answered (iOS only so far). */
    name: 'satisfaction_selected',
    platforms: ['ios'],
    params: ['state', 'source'],
  },
  {
    /** The satisfaction sheet was closed without an answer (iOS only so far). */
    name: 'satisfaction_prompt_dismissed',
    platforms: ['ios'],
    params: ['source'],
  },
  {
    /** The system store review was requested, or "Rate on the store" was tapped in settings. */
    name: 'store_review_started',
    platforms: ['ios', 'android'],
    params: ['source'],
  },
  {
    /** Feedback was submitted (text box or in-app survey; iOS only so far). */
    name: 'feedback_submitted',
    platforms: ['ios'],
    params: ['source', 'method'],
  },

  // ── Server events (GA4 Measurement Protocol, `conversions_service.ts`) ─────
  {
    /** The first time a person used a product line, once per user per product. Carries `product`. */
    name: 'first_use',
    platforms: ['server'],
    params: ['product', 'event_id'],
    meta: { server: { name: 'FirstUse', standard: false } },
    legacy: true,
  },
  {
    /**
     * A person hit a usage limit, once per user per product per month. Carries
     * `product`. Hit from the desktop app it goes to the desktop stream.
     */
    name: 'usage_limit_hit',
    platforms: ['server', 'desktop'],
    params: ['product', 'event_id', 'app_version'],
    meta: { server: { name: 'UsageLimitHit', standard: false } },
    legacy: true,
  },
] as const satisfies readonly AnalyticsEventDefinition[]

type CatalogEntry = (typeof ANALYTICS_EVENT_CATALOG)[number]
type WebTrackedEntry = Extract<CatalogEntry, { key: string }>

/** Every GA4 event name any platform sends. */
export type AnalyticsCatalogEventName = CatalogEntry['name']

/** The web semantic taxonomy fired through `trackEvent`. */
export type WebTrackedEventName = WebTrackedEntry['name']

const catalog: readonly AnalyticsEventDefinition[] = ANALYTICS_EVENT_CATALOG

/** `trackEvent` constant → GA4 name (`ANALYTICS_EVENTS` in apps/web). */
export const WEB_TRACKED_EVENTS = Object.fromEntries(
  catalog.flatMap((event) => (event.key ? [[event.key, event.name]] : []))
) as { readonly [Entry in WebTrackedEntry as Entry['key']]: Entry['name'] }

/** Browser Pixel event for each web-tracked event (`PIXEL_EVENT_MAP` in apps/web). */
export const WEB_PIXEL_EVENT_MAP = Object.fromEntries(
  catalog.flatMap((event) => (event.key ? [[event.name, event.meta.web]] : []))
) as Readonly<Record<WebTrackedEventName, AnalyticsMetaEvent>>

type NameOn<Entry, Platform> = Entry extends {
  readonly name: infer Name
  readonly platforms: readonly (infer Sent)[]
}
  ? Platform extends Sent
    ? Name
    : never
  : never

/** The catalog events `Platform` sends, from the `platforms` column. */
export type AnalyticsEventNameOn<Platform extends AnalyticsPlatform> = NameOn<
  CatalogEntry,
  Platform
>

/** The catalog entry named `name`, if there is one. */
export function analyticsCatalogEvent(name: string): AnalyticsEventDefinition | undefined {
  return catalog.find((event) => event.name === name)
}

/** Whether the catalog says `platform` sends `name`. */
export function isCatalogEventOn(name: string, platform: AnalyticsPlatform): boolean {
  return analyticsCatalogEvent(name)?.platforms.includes(platform) ?? false
}

/** Names the naming rules skip because they shipped before them. */
export const LEGACY_EVENT_NAMES: ReadonlySet<AnalyticsCatalogEventName> = new Set(
  catalog.flatMap((event) => (event.legacy ? [event.name as AnalyticsCatalogEventName] : []))
)
